import 'core-js/web/url';
import 'core-js/web/url-search-params';

export const DSP_VERIFY_REDIRECTING = 'DSP_VERIFY_REDIRECTING';
export const DSP_SESSION_IN_COOKIE = 'DSP_SESSION_IN_COOKIE';
const CORRELATION_KEY = 'ESG_DSP_SESSION_CORRELATION_ID';
const LOGON_ATTEMPT_KEY = 'ESG_DSP_LOGON_ATTEMPT';

let fallbackCorrelationId = null;
let redirecting = false;
let generation = 0;
let pendingAuthentication = null;
let sessionVerified = false;

export const isDspV3Enabled = () => process.env.REACT_APP_CHANNEL_CODE === 'HK';
export const isDspVerifyAuthenticationStatus = status => [400, 401, 403, 419].includes(Number(status));
export const isSuccessStatus = status => status >= 200 && status < 300;
export const getDspGeneration = () => generation;
export const isDspRedirecting = () => redirecting;
export const shouldStopDspResponse = started => redirecting || started !== generation;
export const hasVerifiedDspSession = () => isDspV3Enabled() && sessionVerified;

const clearLogonAttempt = () => {
	try {
		sessionStorage.removeItem(LOGON_ATTEMPT_KEY);
	} catch (error) {
		// This marker is only a redirect safety limit, never authentication proof.
	}
};

export const resetDspSession = () => {
	redirecting = false;
	generation++;
	pendingAuthentication = null;
	sessionVerified = false;
	clearLogonAttempt();
	fallbackCorrelationId = null;
	try {
		sessionStorage.removeItem(CORRELATION_KEY);
	} catch {
		console.warn('Unable to clear DSP session correlation storage.');
	}
};

const httpsUrl = (value, key) => {
	let endpoint = null;
	try {
		endpoint = new URL(value);
	} catch (error) {
		throw new Error('Invalid DSP configuration: ' + key);
	}
	if (endpoint.protocol !== 'https:' || !endpoint.hostname || endpoint.username || endpoint.password || endpoint.hash) {
		throw new Error('Invalid DSP configuration: ' + key);
	}
	return endpoint;
};

export const validateDspV3Config = () => {
	httpsUrl(process.env.REACT_APP_DSP_VERIFY_SECURE_COMM_TOKEN, 'REACT_APP_DSP_VERIFY_SECURE_COMM_TOKEN');
	httpsUrl(process.env.REACT_APP_DSP_LOGON_URL, 'REACT_APP_DSP_LOGON_URL');
};

const isPublicQasBase = endpoint =>
	/^(dev|www)\.risk-profiling-questionnaire\.hsbc\.com\.hk$/.test(endpoint.hostname) &&
	/^\/api\/wpb-gpbw-mmw-hk-hbap-pa-financial-qas-(sct|cert|prod)-proxy\/v0\/?$/.test(endpoint.pathname) &&
	!endpoint.search;

export const isDspCustomerApi = url => {
	if (!isDspV3Enabled()) {
		return false;
	}
	try {
		const endpoint = httpsUrl(new URL(url, window.location.href).href, 'customer API');
		const base = httpsUrl(process.env.REACT_APP_PROXY_TARGET + process.env.REACT_APP_API_HOST, 'customer QAS base');
		const basePath = base.pathname.replace(/\/?$/, '/');
		return isPublicQasBase(base) && endpoint.origin === base.origin &&
			endpoint.pathname.indexOf(basePath) === 0 &&
			!/(^|\/)(gateway|staff|authcontroller|profile)(\/|$)/i.test(endpoint.pathname);
	} catch (error) {
		return false;
	}
};

export const createRequestCorrelationId = () => {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const values = new Uint8Array(10);
	const cryptoProvider = window.crypto && window.crypto.getRandomValues ? window.crypto : window.msCrypto;
	if (cryptoProvider && cryptoProvider.getRandomValues) {
		cryptoProvider.getRandomValues(values);
	} else {
		// Trace IDs only, not security tokens.
		for (let i = 0; i < values.length; i++) {
			values[i] = Math.floor(Math.random() * 256);
		}
	}
	return Array.from(values, value => chars[value % chars.length]).join('');
};

export const getSessionCorrelationId = () => {
	try {
		const stored = sessionStorage.getItem(CORRELATION_KEY);
		if (stored) {
			fallbackCorrelationId = stored;
			return stored;
		}
	} catch (error) {
		// Fall back to a page-scoped trace ID when storage is blocked.
	}
	fallbackCorrelationId = fallbackCorrelationId || createRequestCorrelationId();
	try {
		sessionStorage.setItem(CORRELATION_KEY, fallbackCorrelationId);
	} catch (error) {
		// Retain the same in-memory ID even if setItem fails.
	}
	return fallbackCorrelationId;
};

export const getInitialAccess = () => new URL(window.location.href).searchParams.get('initialAccess') === 'true';

export const buildDspReturnUrl = session => {
	const returnUrl = new URL(window.location.href);
	returnUrl.searchParams.delete('SAAS_TOKEN_ID');
	const queryIndex = returnUrl.hash.indexOf('?');
	const hashParams = new URLSearchParams(queryIndex === -1 ? '' : returnUrl.hash.slice(queryIndex + 1));
	if (queryIndex !== -1 && hashParams.has('SAAS_TOKEN_ID')) {
		hashParams.delete('SAAS_TOKEN_ID');
		const query = hashParams.toString();
		returnUrl.hash = returnUrl.hash.slice(0, queryIndex) + (query ? '?' + query : '');
	}
	if (session) {
		['model', 'locale', 'backUrl'].forEach(key => {
			if (key === 'locale' && /\/(en-US|zh-HK|zh-CN)\/?$/i.test(returnUrl.pathname)) {
				return;
			}
			if (!returnUrl.searchParams.has(key) && !hashParams.has(key) && session[key] !== null && session[key] !== undefined) {
				returnUrl.searchParams.set(key, session[key]);
			}
		});
	}
	return returnUrl.toString();
};

export const triggerDspLogon = session => {
	if (redirecting) {
		return DSP_VERIFY_REDIRECTING;
	}
	validateDspV3Config();
	const logon = new URL(process.env.REACT_APP_DSP_LOGON_URL);
	logon.searchParams.delete('initialAccess');
	if (getInitialAccess()) {
		logon.searchParams.set('initialAccess', 'true');
	}
	logon.searchParams.set('targetCamLevel', '30');
	logon.searchParams.set('returnUrl', buildDspReturnUrl(session));

	sessionVerified = false;
	redirecting = true;
	try {
		window.location.href = logon.toString();
	} catch (error) {
		redirecting = false;
		clearLogonAttempt();
		throw error;
	}
	return DSP_VERIFY_REDIRECTING;
};

const requireSuccess = (response, message) => {
	if (!isSuccessStatus(response.status)) {
		const error = new Error(message);
		error.status = response.status;
		throw error;
	}
	return DSP_SESSION_IN_COOKIE;
};

const authenticate = async (session, started) => {
	validateDspV3Config();
	const response = await fetch(process.env.REACT_APP_DSP_VERIFY_SECURE_COMM_TOKEN, {
		method: 'POST', mode: 'cors', cache: 'no-cache', credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'X-HSBC-Session-Correlation-ID': getSessionCorrelationId(),
			'X-HSBC-Request-Correlation-ID': createRequestCorrelationId()
		}
	});
	if (shouldStopDspResponse(started)) {
		return DSP_VERIFY_REDIRECTING;
	}
	if (isDspVerifyAuthenticationStatus(response.status)) {
		return triggerDspLogon(session);
	}
	const result = requireSuccess(response, 'DSP secure communication verification failed.');
	sessionVerified = true;
	clearLogonAttempt();
	return result;
};

export const authenticateDsp = (_headers, session) => {
	if (redirecting) {
		return Promise.resolve(DSP_VERIFY_REDIRECTING);
	}
	if (!pendingAuthentication) {
		const started = generation;
		const clearPending = () => {
			if (started === generation) {
				pendingAuthentication = null;
			}
		};
		pendingAuthentication = authenticate(session, started).then(result => {
			clearPending();
			return result;
		}, error => {
			clearPending();
			if (shouldStopDspResponse(started)) {
				return DSP_VERIFY_REDIRECTING;
			}
			throw error;
		});
	}
	return pendingAuthentication;
};